// hooks/useMemberNavigation.js
import { useNavigate } from 'react-router-dom';
import { extractMemberData, MEMBER_TYPES, getMemberConfig, formatDate } from '../utils/membersConfig';

const useMemberNavigation = () => {
  const navigate = useNavigate();

  const getDetailRoute = (memberType) => {
    const config = getMemberConfig(memberType);
    return config.detailRoute;
  };

  /**
   * Detect member type from raw data
   */
   const detectMemberType = (member) => {
    if (member.president_num !== undefined) {
      return MEMBER_TYPES.PRESIDENT;
    }
    // Vice presidents have full_name but no president_num and no terms array
    if (member.full_name && !member.terms && !member.president_num) {
      // Check if it's from vice_presidents endpoint (has id but no president_num)
      // This is a bit tricky - we might need to pass memberType explicitly
      return MEMBER_TYPES.VICE_PRESIDENT; // or JUSTICE - need to differentiate
    }
    if (member.terms) {
      const latestTerm = member.terms[member.terms.length - 1];
      if (latestTerm?.chamber === 'Senate') {
        return MEMBER_TYPES.SENATE;
      }
      return MEMBER_TYPES.HOUSE;
    }
    return MEMBER_TYPES.HOUSE;
  };

  /**
   * Navigate to member detail page
   */
  const navigateToMemberFromRaw = (rawMember, memberType) => {
    if (!rawMember) {
      console.warn('useMemberNavigation: No member data provided');
      return;
      
    }
    

    const detectedType = memberType || detectMemberType(rawMember);
    const config = getMemberConfig(detectedType);
    const route = config.detailRoute;

    window.scrollTo(0, 0);

    // Handle President/Justice navigation (different data structure)
    if (config.dataType === 'executive' || config.dataType === 'judicial') {
      // Use president_num or name-based ID
      const id = rawMember.president_num?.toString() || 
                 rawMember.full_name?.replace(/\s+/g, '-').toLowerCase();
      
      navigate(`${route}/${id}`, {
        state: {
          name: rawMember.full_name,
          termStart: rawMember.term_start_date,
          termEnd: rawMember.term_end_date,
          presidentNumber: rawMember.president_num,
          imageSource: rawMember.url,
          description: rawMember.description,
        }
      });
      return;
    }

    // Handle Congress member navigation (House/Senate)
    const memberData = extractMemberData(rawMember, detectedType);
    
    navigate(`${route}/${memberData.bioGuideId}`, {
      state: {
        name: memberData.fullName,
        webURL: memberData.websiteUrl,
        imageSource: memberData.imageUrl,
        chamber: memberData.chamber,
        startYear: memberData.startYear,
        stateName: memberData.stateName,
        party: memberData.partyHistory,
        district: memberData.district,
        address: memberData.addressInfo,
      }
    });
  };

  /**
   * Legacy navigation method for backwards compatibility
   */
  const navigateWithParams = (bioGuideId, name, webURL, imageSource, chamber, startYear, stateName, party, district, address, memberType) => {
    if (!bioGuideId) {
      console.warn('useMemberNavigation: No bioGuideId provided');
      return;
    }

    const detectedType = memberType || (
      chamber === 'Senate' ? MEMBER_TYPES.SENATE :
      chamber === 'President' ? MEMBER_TYPES.PRESIDENT :
      chamber === 'Supreme Court' ? MEMBER_TYPES.JUSTICE :
      MEMBER_TYPES.HOUSE
    );
    
    const route = getDetailRoute(detectedType);
    window.scrollTo(0, 0);
    
    navigate(`${route}/${bioGuideId}`, {
      state: { name, webURL, imageSource, chamber, startYear, stateName, party, district, address }
    });
  };

  return {
    navigateToMemberFromRaw,
    navigateWithParams,
    getDetailRoute,
    detectMemberType,
  };
};

export default useMemberNavigation;